import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { CSSTransition } from "react-transition-group";
import { Button, Input, Loader, Segment, Tab } from "semantic-ui-react";
import { toast } from "../..";
import { UserRole } from "../../actions/authentificationActions";
import { IChangeLogItem } from "../../models/changeLogItem";
import {
    exportChangeLogItems,
    getChangeLogICSItems,
    getChangeLogItems,
} from "../../api/changeLogs";
import { AppState } from "../../store/configureStore";
import { ChangeLogTable } from "./ChangeLogTable";
import "./ChangeLog.css";
import { ChangeLogICSTable } from "./ChangeLogICSTable";

export const ChangeLog = () => {
    const intl = useIntl();
    const user = useSelector((state: AppState) => state.user);
    const [items, setItems] = useState<IChangeLogItem[]>([]);
    const [totalRows, setTotalRows] = useState(-1);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState("");

    const [itemsIcs, setItemsIcs] = useState<IChangeLogItem[]>([]);
    const [totalRowsIcs, setTotalRowsIcs] = useState(-1);
    const [pageIcs, setPageIcs] = useState(1);
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const [queryIcs, setQueryIcs] = useState("");

    const fetchChangeLogItems = useCallback(
        async query => {
            try {
                setLoading(true);
                setItems([]);
                setTotalRows(-1);
                await new Promise(r => setTimeout(r, 100));
                const res = await getChangeLogItems(page, query);
                setItems(res.result as IChangeLogItem[]);
                setTotalRows(res.totalRows);
            } catch {
                toast(intl.formatMessage({ id: "unableToFetch" }), false);
            } finally {
                setLoading(false);
            }
        },
        [page, intl]
    );

    const fetchChangeLogICSItems = useCallback(
        async queryIcs => {
            try {
                setLoading(true);
                setItemsIcs([]);
                setTotalRowsIcs(-1);
                await new Promise(r => setTimeout(r, 100));
                const res = await getChangeLogICSItems(pageIcs, queryIcs, user.language);
                setItemsIcs(res.result as IChangeLogItem[]);
                setTotalRowsIcs(res.totalRows);
            } catch {
                toast(intl.formatMessage({ id: "unableToFetch" }), false);
            } finally {
                setLoading(false);
            }
        },
        [pageIcs, intl, user.language]
    );

    const exportChangeLogs = async () => {
        setLoading(true);
        try {
            await exportChangeLogItems();
        } catch {
            toast(intl.formatMessage({ id: "failedToExport" }), false);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e: any) =>
        e.key === "Enter" || e.charCode === 13 ? fetchChangeLogItems(query) : null;

    const handleKeyPressIcs = (e: any) =>
        e.key === "Enter" || e.charCode === 13 ? fetchChangeLogICSItems(queryIcs) : null;

    const handleTableChange = (activeIndex: any) => {
        setActiveIndex(activeIndex);
    };

    useEffect(() => {
        if (user.role === UserRole.PowerUser) !query && fetchChangeLogItems("");
    }, [fetchChangeLogItems, query, user.role]);

    useEffect(() => {
        !queryIcs && fetchChangeLogICSItems("");
    }, [fetchChangeLogICSItems, queryIcs]);

    useEffect(() => {
        if (user.role === UserRole.OfficeUserViadonau) setActiveIndex(1);
    }, [user.role]);

    return (
        <div>
            <div className="container-header">
                <h1>{intl.formatMessage({ id: "changeLog" })}</h1>
                <Button
                    primary
                    content={intl.formatMessage({ id: "exportChangeLog" })}
                    onClick={() => exportChangeLogs()}
                    disabled={loading}
                    loading={loading}
                />
            </div>
            <section className="change-log-container">
                <Segment className="grey-segment">
                    <section className="input-container">
                        <label>{intl.formatMessage({ id: "search" })}</label>
                        <Input
                            value={activeIndex === 0 ? query : queryIcs}
                            onChange={e =>
                                activeIndex === 0
                                    ? setQuery(e.target.value)
                                    : setQueryIcs(e.target.value)
                            }
                            onKeyPress={(e: any) =>
                                activeIndex === 0 ? handleKeyPress(e) : handleKeyPressIcs(e)
                            }
                        />
                    </section>
                    <Button
                        primary
                        content={intl.formatMessage({ id: "search" })}
                        onClick={() =>
                            activeIndex === 0
                                ? fetchChangeLogItems(query)
                                : fetchChangeLogICSItems(queryIcs)
                        }
                    />
                </Segment>

                {user.role === UserRole.PowerUser && (
                    <Tab
                        onTabChange={(e, index) => handleTableChange(index.activeIndex)}
                        style={{ borderRadius: "0px" }}
                        panes={[
                            {
                                menuItem: intl.formatMessage({ id: "icsAtNcdbTab" }),
                                render: () => (
                                    <Tab.Pane
                                        loading={loading}
                                        className="tab-style"
                                        attached={true}
                                    >
                                        <CSSTransition
                                            in={totalRows > -1}
                                            timeout={100}
                                            classNames="fade"
                                            unmountOnExit
                                        >
                                            <ChangeLogTable
                                                items={items}
                                                page={page}
                                                setPage={setPage}
                                                totalRows={totalRows}
                                            />
                                        </CSSTransition>
                                    </Tab.Pane>
                                ),
                            },
                            {
                                menuItem: intl.formatMessage({ id: "icsViadonauTab" }),
                                render: () => (
                                    <Tab.Pane
                                        loading={loading}
                                        className="tab-style"
                                        attached={true}
                                    >
                                        <CSSTransition
                                            in={totalRows > -1}
                                            timeout={100}
                                            classNames="fade"
                                            unmountOnExit
                                        >
                                            <ChangeLogICSTable
                                                items={itemsIcs}
                                                page={pageIcs}
                                                setPage={setPageIcs}
                                                totalRows={totalRowsIcs}
                                            />
                                        </CSSTransition>
                                    </Tab.Pane>
                                ),
                            },
                        ]}
                    />
                )}

                {user.role === UserRole.OfficeUserViadonau && (
                    <>
                        {loading ? (
                            <Loader active inline="centered" size="huge" />
                        ) : (
                            <CSSTransition
                                in={totalRowsIcs > -1}
                                timeout={100}
                                classNames="fade"
                                unmountOnExit
                            >
                                <ChangeLogICSTable
                                    items={itemsIcs}
                                    page={pageIcs}
                                    setPage={setPageIcs}
                                    totalRows={totalRowsIcs}
                                />
                            </CSSTransition>
                        )}
                    </>
                )}
            </section>
        </div>
    );
};
